import React, { useCallback, useEffect, useState } from 'react';
import { Building2, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import BrokerageProfileForm from '@/components/BrokerageProfileForm';
import BrokerageProfileStatus from '@/components/BrokerageProfileStatus';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', red: '#E24B4A', slate: '#5C6B7A' };

const setPath = (obj, path, value) => {
  const keys = path.split('.');
  const next = { ...obj };
  let cur = next;
  keys.slice(0, -1).forEach(k => { cur[k] = { ...(cur[k] || {}) }; cur = cur[k]; });
  cur[keys[keys.length - 1]] = value;
  return next;
};

export default function BrokerageProfilePage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const fromServer = d => ({ abn: d.abn, licence: d.licence, contact: d.contact });
  const load = useCallback(() => api.get('/broker/brokerage').then(r => {
    setData(r.data);
    setForm(f => f ?? fromServer(r.data));   // never clobber what the broker is typing
  }).catch(e => setError(e?.response?.data?.detail || 'Could not load your brokerage')), [api]);
  useEffect(() => { load(); }, [load]);

  const setField = (path, value) => { setForm(f => setPath(f, path, value)); setErrors(e => ({ ...e, [path]: undefined })); };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/broker/brokerage', form);
      setData(r.data); setForm(fromServer(r.data)); setErrors({});
      toast.success(r.data.completeness.complete ? 'Brokerage profile complete' : `Saved — ${r.data.completeness.missing.length} detail(s) still to add`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.errors) { setErrors(d.errors); toast.error(d.message || 'Some details need attention'); }
      else toast.error(typeof d === 'string' ? d : 'Could not save');
    } finally { setSaving(false); }
  };

  if (error) return <div className="p-6 text-sm" style={{ color: C.red }} data-testid="brokerage-profile-error">{error}</div>;
  if (!data || !form) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 rounded-md" />)}</div>;

  return (
    <div className="space-y-5" data-testid="brokerage-profile-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: C.navy }}><Building2 className="w-4 h-4" style={{ color: C.blue }} />{data.name}</h2>
          <p className="text-xs mt-0.5" style={{ color: C.slate }}>
            {[[data.city, data.state].filter(Boolean).join(', '), data.profile?.archetype, data.specialty].filter(Boolean).concat('what you record here is what appears on your schedules and invoices').join(' · ')}
          </p>
        </div>
        <button onClick={save} disabled={saving} className="flex items-center gap-1.5 px-3.5 py-2 rounded-md text-xs font-semibold text-white disabled:opacity-50" style={{ background: C.blue }} data-testid="brokerage-save-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save details
        </button>
      </div>
      <BrokerageProfileStatus data={data} />
      <Card className="rounded-md p-4"><BrokerageProfileForm form={form} setField={setField} errors={errors} meta={data} /></Card>
      <div className="grid md:grid-cols-2 gap-3">
        <Card className="rounded-md p-4" data-testid="brokerage-operators">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: C.blue }}>People at {data.name}</h3>
          <ul className="text-xs space-y-1" style={{ color: C.navy }}>{data.operators.map((o, i) => <li key={i}>{o.name} <span style={{ color: C.slate }}>· {o.title}</span></li>)}</ul>
        </Card>
        <Card className="rounded-md p-4" data-testid="brokerage-history">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: C.blue }}>Changes</h3>
          {data.history.length === 0 ? <p className="text-xs" style={{ color: C.slate }}>No details recorded yet.</p> : (
            <ul className="text-[11px] space-y-1.5">{data.history.map((h, i) => (
              <li key={i} style={{ color: C.navy }}><span style={{ color: C.slate }}>{(h.at || '').slice(0, 16).replace('T', ' ')} · {h.by}</span><br />
                {Object.entries(h.changes).map(([k, v]) => `${k}: ${v.from ?? '—'} → ${v.to ?? '—'}`).join('; ')}</li>
            ))}</ul>
          )}
        </Card>
      </div>
    </div>
  );
}
